import { useState } from 'react';
import { usePermission } from '../hooks/usePermission';
// eslint-disable-next-line no-unused-vars
import { FaLock, FaInfoCircle } from 'react-icons/fa';

/**
 * 権限制御付きボタンコンポーネント
 * ユーザーの権限レベルに応じてボタンの有効/無効を制御
 */
const PermissionButton = ({
  action,
  resource,
  ownerId,
  onClick,
  children,
  className = '',
  showTooltip = true,
  showModal = true,
  ...props
}) => {
  const {
    canCreate,
    canUpdate,
    canDelete,
    canPerform,
    getPermissionMessage,
    navigateToUpgrade,
    permissionLevel,
  } = usePermission();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * 権限チェック
   */
  const isAllowed = () => {
    switch (action) {
      case 'create':
        return canCreate(resource);
      case 'update':
        return canUpdate(resource, ownerId);
      case 'delete':
        return canDelete(resource, ownerId);
      case 'perform':
        return canPerform(resource);
      default:
        return true; // Read操作は基本的に許可
    }
  };

  const allowed = isAllowed();
  const message = getPermissionMessage(action);

  /**
   * クリックハンドラー（連打防止機能付き）
   */
  const handleClick = async (e) => {
    // 連打防止: 処理中は無視
    if (isProcessing) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (!allowed) {
      e.preventDefault();
      e.stopPropagation();

      if (showModal) {
        setShowUpgradeModal(true);
      }
      return;
    }

    if (onClick) {
      setIsProcessing(true);
      try {
        await onClick(e);
      } catch (error) {
        console.error('❌ Button action error:', error);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  /**
   * 権限レベル・処理状態に応じたボタンスタイル
   */
  const getButtonStyle = () => {
    if (!allowed || isProcessing) {
      return `${className} opacity-50 cursor-not-allowed relative`;
    }
    return className;
  };

  /**
   * 権限レベルに応じたラベル取得
   */
  const getPermissionLabel = () => {
    switch (permissionLevel) {
      case 0:
        return 'ログインが必要';
      case 1:
        return '準会員以上';
      case 2:
        return '本会員限定';
      default:
        return '';
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        className={getButtonStyle()}
        disabled={!allowed || isProcessing}
        title={!allowed && showTooltip ? message : undefined}
        {...props}
      >
        <span className="flex items-center gap-2">
          {isProcessing ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              処理中...
            </>
          ) : (
            <>
              {children}
              {!allowed && <FaLock size={12} className="text-gray-500" />}
            </>
          )}
        </span>
      </button>

      {/* アップグレード促進モーダル */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-3 mb-4">
              <FaInfoCircle className="text-blue-500 text-xl flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  権限が不足しています
                </h3>
                <p className="text-gray-600 text-sm">{message}</p>
              </div>
            </div>

            {/* 権限レベル表示 */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="text-sm text-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <span>現在の権限レベル:</span>
                  <span className="font-semibold">
                    {permissionLevel === 0 && '未ログイン'}
                    {permissionLevel === 1 && 'ゲスト'}
                    {permissionLevel === 2 && '準会員'}
                    {permissionLevel === 3 && '本会員'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>必要な権限レベル:</span>
                  <span className="font-semibold text-blue-600">
                    {getPermissionLabel()}
                  </span>
                </div>
              </div>
            </div>

            {/* 機能説明 */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-2">
                アップグレードすると利用できる機能:
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {permissionLevel < 2 && (
                  <React.Fragment key="level2">
                    <li key="question">• 問いの投稿と議論への参加</li>
                    <li key="desire">• 欲求の投稿と共鳴</li>
                    <li key="friends">• 友達機能とチャット</li>
                  </React.Fragment>
                )}
                {permissionLevel < 3 && (
                  <React.Fragment key="level3">
                    <li key="project">• プロジェクトの作成と管理</li>
                    <li key="store">• ストアの開設と商品販売</li>
                    <li key="dao">• DAOの立ち上げと運営</li>
                    <li key="vote">• 投票への参加</li>
                  </React.Fragment>
                )}
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                key="upgrade"
                onClick={() => {
                  navigateToUpgrade();
                  setShowUpgradeModal(false);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                アップグレードする
              </button>
              <button
                key="later"
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                後で
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PermissionButton;
